// Widget registry for passthrough markup widgets. The importer maps rare Alpaca
// widgets (country, state, image, chem_doodle, chem_writer) to `type: "markup"`
// with `config.widget` set (alpaca_importer.rb WIDGET_PASSTHROUGH). MarkupField
// routes those to a registered widget component — a host override if supplied,
// otherwise a safe built-in fallback that preserves the field's value shape and
// warns once (non-production) that no real widget is installed.
//
// Full widget behavior (ChemDoodle/ChemWriter editors, country/state pickers)
// is intentionally NOT bundled here — those require host-provided globals and
// data. We ship value-preserving fallbacks until fixture counts justify more.

import type { WidgetComponent, WidgetComponentOverrides } from '../rendererContext'
import { TextPassthroughWidget, ReadonlyPassthroughWidget } from './PassthroughWidgets'

/** Built-in fallbacks keyed by `config.widget`. */
const DEFAULT_WIDGETS: Record<string, WidgetComponent> = {
  // Choice-like widgets we don't have option data for → editable text.
  country: TextPassthroughWidget,
  state: TextPassthroughWidget,
  // Rich widgets requiring host globals/data → value-preserving read-only view.
  image: ReadonlyPassthroughWidget,
  chem_doodle: ReadonlyPassthroughWidget,
  chem_writer: ReadonlyPassthroughWidget,
}

/** The widget names that have a built-in fallback. */
export const KNOWN_WIDGETS = Object.keys(DEFAULT_WIDGETS)

const warned = new Set<string>()

/** Warn once per widget name in non-production that no real widget is installed. */
export function warnWidgetFallback(widget: string, hasFallback: boolean): void {
  if (warned.has(widget)) return
  warned.add(widget)
  const env = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process
    ?.env
  if (env?.NODE_ENV === 'production') return
  const detail = hasFallback
    ? 'rendering a value-preserving fallback'
    : 'rendering raw markup'
  // eslint-disable-next-line no-console
  console.warn(
    `[dynamic-form-renderer] no widgetComponents entry for ${JSON.stringify(widget)} — ${detail}. ` +
      'Supply a host widget via the widgetComponents prop for full behavior.',
  )
}

/** Reset the warn-once cache. Test seam only. */
export function resetWidgetWarnings(): void {
  warned.clear()
}

/**
 * Resolve a widget renderer: host override → built-in fallback → undefined
 * (caller renders raw markup). Warns once when falling back off a host override.
 */
export function resolveWidgetComponent(
  widget: string,
  overrides: WidgetComponentOverrides,
): WidgetComponent | undefined {
  const override = overrides[widget]
  if (override) return override
  const fallback = DEFAULT_WIDGETS[widget]
  warnWidgetFallback(widget, fallback != null)
  return fallback
}

export { TextPassthroughWidget, ReadonlyPassthroughWidget }
